import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Anchor, Ship, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Marine = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/marine"]?.title} description={seoData["industries/marine"]?.description} path="/industries/marine" keywords={seoData["industries/marine"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Marine & Offshore"
        description="Marine coating contractor serving California ports, shipyards, and offshore facilities. Corrosion-resistant coatings for vessels, ballast tanks, port infrastructure, and offshore platforms."
        backgroundKey="marine"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Saltwater immersion corrosion on hulls, ballast tanks, and sea chests',
              'Splash zone erosion on pilings, fenders, and dock structures',
              'Cathodic disbondment and coating failure on submerged steel',
              'Concrete spalling on wharves, seawalls, and marine structures',
              'Biofouling and marine growth on intake structures',
              'Corrosion of port cranes, gantries, and material handling equipment'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg">
                <Anchor className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Ballast Tank & Hull Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Marine-grade epoxy coating systems for ballast tanks, void spaces, cargo holds, and hull protection. Surface-tolerant systems for application in confined spaces with limited ventilation.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Port Infrastructure Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Concrete and steel repair for wharves, pilings, fenders, and seawalls. Splash zone protection, pile jacket installation, and cathodic protection for marine structures.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Offshore Platform Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion protection for offshore platforms, risers, and subsea equipment. Splash zone repair, deck coating, and structural steel protection in harsh marine environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Marine Equipment Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Rudder and stern tube repair, sea chest restoration, and pump rebuild for marine vessels and port equipment using cold-applied epoxy composites.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Marine & Offshore?</h2>
              <div className="space-y-3">
                {['TWIC credentialed crews for port and terminal access',
                  'Surface-tolerant coating systems for damp and humid conditions',
                  'In-water and splash zone repair capabilities',
                  'Rapid mobilization for emergency vessel and port repairs',
                  'OSHA Maritime certified crews',
                  'IMO and USCG compliant coating systems'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">California Marine Contractor</h3>
              <p className="text-blue-100 mb-6">BCS serves major California ports, shipyards, and offshore operators. Our crews are TWIC credentialed and OSHA Maritime certified for safe work in marine environments.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/structural-repair')}>
                View Structural Repair <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Marine Assets</h2>
          <p className="text-blue-100 mb-8">Contact our marine coating specialists for port, vessel, or offshore corrosion repair.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Marine;
